import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { ChevronDownIcon, ChevronRightIcon, FileTextIcon, ChevronsUpDownIcon } from 'lucide-react';
import { mockDiscoverySets } from '../data/mockData';
const RESPONSE_MAX_HEIGHT = 160;
function ResponseBlock({
  text,
  questionId
}: {
  text: string;
  questionId: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > RESPONSE_MAX_HEIGHT);
    }
  }, [text]);
  return <div className="bg-navy rounded-sm border border-navy-border" data-id="element-120">
      <div className="flex items-center justify-between px-3 pt-2 pb-1" data-id="element-121">
        <div className="text-xxs font-semibold text-text-muted uppercase tracking-wider" data-id="element-122">
          Response
        </div>
        {isOverflowing && <button onClick={e => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
      }} className="text-xxs text-accent-amber hover:text-accent-amber/80 font-medium flex items-center gap-1 transition-colors" data-id="element-123">
            <ChevronsUpDownIcon size={12} data-id="element-124" />
            {isExpanded ? 'Collapse' : 'Show Full Response'}
          </button>}
      </div>
      <div className="relative" data-id="element-125">
        <div ref={contentRef} className="px-3 pb-3 overflow-hidden transition-[max-height] duration-300 ease-in-out" style={{
        maxHeight: isExpanded ? `${contentRef.current?.scrollHeight ?? 9999}px` : `${RESPONSE_MAX_HEIGHT}px`
      }} data-id="element-126">
          <p className="text-white text-sm leading-relaxed whitespace-pre-line" data-id="element-127">
            {text}
          </p>
        </div>
        {isOverflowing && !isExpanded && <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-navy to-transparent pointer-events-none rounded-b-sm" data-id="element-128" />}
      </div>
    </div>;
}
export function DiscoveryTable() {
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set(['set-001']));
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const toggleSet = (id: string) => {
    const newExpanded = new Set(expandedSets);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSets(newExpanded);
  };
  // Build flat navigation list: sets + expanded questions
  const getNavItems = useCallback(() => {
    const items: string[] = [];
    mockDiscoverySets.forEach(set => {
      items.push(set.id);
      if (expandedSets.has(set.id) && set.questions && set.questions.length > 0) {
        set.questions.forEach(q => items.push(q.id));
      }
    });
    return items;
  }, [expandedSets]);
  const scrollToItem = useCallback((id: string) => {
    const el = itemRefs.current.get(id);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, []);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const navItems = getNavItems();
    if (navItems.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = focusedId ? navItems.indexOf(focusedId) : -1;
      let nextIndex: number;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < navItems.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : navItems.length - 1;
      }
      const nextId = navItems[nextIndex];
      setFocusedId(nextId);
      scrollToItem(nextId);
    }
    if (e.key === 'Enter' && focusedId) {
      e.preventDefault();
      // If focused on a set, toggle expand
      const isSet = mockDiscoverySets.some(s => s.id === focusedId);
      if (isSet) {
        toggleSet(focusedId);
      }
    }
    if (e.key === 'Escape') {
      setFocusedId(null);
    }
  }, [focusedId, getNavItems, scrollToItem, toggleSet]);
  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(id, el);
    } else {
      itemRefs.current.delete(id);
    }
  }, []);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Complete':
        return 'text-status-green bg-status-green/10 border-status-green/20';
      case 'Partial':
        return 'text-status-amber bg-status-amber/10 border-status-amber/20';
      case 'Pending':
        return 'text-text-secondary bg-navy-surface border-navy-border';
      case 'Overdue':
        return 'text-status-red bg-status-red/10 border-status-red/20';
      default:
        return 'text-text-secondary bg-navy-surface border-navy-border';
    }
  };
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Interrogatory':
        return 'bg-navy-elevated text-text-secondary border-navy-border';
      case 'RFP':
        return 'bg-[#1E3A5F] text-[#8CB4E6] border-[#2A4B75]';
      case 'RFA':
        return 'bg-[#3D2B1F] text-[#E6B48C] border-[#5C4333]';
      default:
        return 'bg-navy-elevated text-text-secondary border-navy-border';
    }
  };
  const getTagStyle = (tag: string) => {
    if (tag === 'Objection' || tag === 'Privilege' || tag === 'Evasive') {
      return 'bg-status-red/10 text-status-red border-status-red/20';
    }
    if (tag === 'Refers to Documents') {
      return 'bg-[#1E3A5F] text-[#8CB4E6] border-[#2A4B75]';
    }
    return 'bg-navy-elevated text-text-secondary border-navy-border';
  };
  return <div ref={containerRef} className="flex-1 overflow-auto bg-navy outline-none" tabIndex={0} onKeyDown={handleKeyDown} data-id="element-129">
      <table className="w-full text-left border-collapse" data-id="element-130">
        <thead className="bg-navy-elevated sticky top-0 z-10 shadow-sm" data-id="element-131">
          <tr data-id="element-132">
            <th className="w-8 p-3 border-b border-navy-border" data-id="element-133"></th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-134">
              Set Name
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-135">
              Type
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-136">
              From → To
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-137">
              Date Served
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-138">
              Q's
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-139">
              Status
            </th>
            <th className="p-3 border-b border-navy-border text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-140">
              PDF
            </th>
          </tr>
        </thead>
        <tbody className="text-sm" data-id="element-141">
          {mockDiscoverySets.map(set => {
          const isSetFocused = focusedId === set.id;
          return <Fragment key={set.id} data-id="element-142">
                <tr ref={el => registerRef(set.id, el)} className={`border-b border-navy-border transition-colors cursor-pointer
                    ${expandedSets.has(set.id) ? 'bg-navy-surface/50' : ''}
                    ${isSetFocused ? 'bg-navy-surface ring-1 ring-inset ring-accent-amber/40' : 'hover:bg-navy-surface'}`} onClick={() => {
              setFocusedId(set.id);
              toggleSet(set.id);
            }} data-id="element-143">
                  <td className="p-3 text-text-muted" data-id="element-144">
                    {isSetFocused && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent-amber rounded-r-sm" data-id="element-145" />}
                    {expandedSets.has(set.id) ? <ChevronDownIcon size={16} data-id="element-146" /> : <ChevronRightIcon size={16} data-id="element-147" />}
                  </td>
                  <td className="p-3 font-medium text-white truncate max-w-[300px]" title={set.setName} data-id="element-148">
                    {set.setName}
                  </td>
                  <td className="p-3" data-id="element-149">
                    <span className={`text-xxs px-2 py-0.5 rounded-sm border ${getTypeBadge(set.type)}`} data-id="element-150">
                      {set.type}
                    </span>
                  </td>
                  <td className="p-3 text-text-secondary text-xs" data-id="element-151">
                    <span className="text-white" data-id="element-152">{set.from}</span>{' '}
                    <span className="text-text-muted mx-1" data-id="element-153">→</span>{' '}
                    <span className="text-white" data-id="element-154">{set.to}</span>
                  </td>
                  <td className="p-3 text-text-secondary text-xs" data-id="element-155">
                    {set.dateServed}
                  </td>
                  <td className="p-3 text-text-secondary text-xs" data-id="element-156">
                    {set.questionCount}
                  </td>
                  <td className="p-3" data-id="element-157">
                    <span className={`text-xxs px-2 py-0.5 rounded-sm border font-medium ${getStatusColor(set.status)}`} data-id="element-158">
                      {set.status}
                    </span>
                  </td>
                  <td className="p-3" data-id="element-159">
                    <button className="text-text-muted hover:text-accent-amber transition-colors" title={set.sourcePdf} onClick={e => e.stopPropagation()} data-id="element-160">
                      <FileTextIcon size={16} data-id="element-161" />
                    </button>
                  </td>
                </tr>

                {expandedSets.has(set.id) && set.questions && set.questions.length > 0 && <tr data-id="element-162">
                      <td colSpan={8} className="p-0 border-b border-navy-border bg-[#0A121C]" data-id="element-163">
                        <div className="pl-12 pr-6 py-4 space-y-4" data-id="element-164">
                          {set.questions.map(q => {
                    const isQFocused = focusedId === q.id;
                    return <div key={q.id} ref={el => registerRef(q.id, el)} onClick={() => setFocusedId(q.id)} className={`panel p-4 flex flex-col gap-3 border-l-2 transition-all cursor-pointer
                                ${isQFocused ? 'border-l-accent-amber bg-navy-surface/60 ring-1 ring-inset ring-accent-amber/30' : 'border-l-navy-border hover:border-l-accent-amber'}`} data-id="element-165">
                                <div className="flex justify-between items-start gap-4" data-id="element-166">
                                  <div className="flex-1" data-id="element-167">
                                    <div className="flex items-center gap-2 mb-1" data-id="element-168">
                                      <span className="font-semibold text-white text-xs" data-id="element-169">
                                        {q.questionNumber}
                                      </span>
                                      {isQFocused && <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-accent-amber/10 text-accent-amber border border-accent-amber/20 font-medium" data-id="element-170">
                                          ●
                                        </span>}
                                      <div className="flex gap-1" data-id="element-171">
                                        {q.tags.map(tag => <span key={tag} className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${getTagStyle(tag)}`} data-id="element-172">
                                            {tag}
                                          </span>)}
                                      </div>
                                    </div>
                                    <p className="text-text-secondary text-sm leading-relaxed mb-3" data-id="element-173">
                                      {q.questionText}
                                    </p>
                                  </div>
                                </div>
                                <ResponseBlock text={q.responseText} questionId={q.id} data-id="element-174" />
                              </div>;
                  })}
                        </div>
                      </td>
                    </tr>}

                {expandedSets.has(set.id) && (!set.questions || set.questions.length === 0) && <tr data-id="element-175">
                      <td colSpan={8} className="p-0 border-b border-navy-border bg-[#0A121C]" data-id="element-176">
                        <div className="pl-12 pr-6 py-6 text-center text-text-muted text-sm italic" data-id="element-177">
                          No responses recorded yet for this set.
                        </div>
                      </td>
                    </tr>}
              </Fragment>;
        })}
        </tbody>
      </table>

      {/* Keyboard hint */}
      <div className="sticky bottom-0 bg-navy-elevated/90 backdrop-blur-sm border-t border-navy-border px-4 py-1.5 flex items-center gap-4 text-xxs text-text-muted" data-id="element-178">
        <span className="flex items-center gap-1.5" data-id="element-179">
          <kbd className="bg-navy-surface border border-navy-border rounded px-1.5 py-0.5 font-mono text-[10px]" data-id="element-180">
            ↑
          </kbd>
          <kbd className="bg-navy-surface border border-navy-border rounded px-1.5 py-0.5 font-mono text-[10px]" data-id="element-181">
            ↓
          </kbd>
          Navigate
        </span>
        <span className="flex items-center gap-1.5" data-id="element-182">
          <kbd className="bg-navy-surface border border-navy-border rounded px-1.5 py-0.5 font-mono text-[10px]" data-id="element-183">
            Enter
          </kbd>
          Expand / Collapse
        </span>
        <span className="flex items-center gap-1.5" data-id="element-184">
          <kbd className="bg-navy-surface border border-navy-border rounded px-1.5 py-0.5 font-mono text-[10px]" data-id="element-185">
            Esc
          </kbd>
          Clear Focus
        </span>
      </div>
    </div>;
}